import { Alert, Box, Container, Header, Spinner } from "@amzn/awsui-components-react";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { PERMISSIONS } from "configuration/config";
import { AUTH_ERROR_TEST_ID, AUTH_LOADING_TEST_ID } from "helpers";
import React, { Fragment, FunctionComponent, useEffect, useState } from "react";
import { UserPermissions } from "services/user-permissions";

/**
 * Props interface for the AuthLoading component which handles authentication state and loading.
 * @interface ContainerProps
 * @property {React.ReactNode} children - The content/components to be rendered after successful authentication.
 */
interface ContainerProps {
    children: React.ReactNode;
}

const getStringPath: string = "common.component.authLoading";

/**
 * Authenticating component to verify is user is authenticated before rendering the page
 * Shows a loading spinner during initial Authentication/Authorization.
 * When the user is authenticated, it renders the children components.
 * If there is an error during authentication, it displays an error message.
 * @param {ContainerProps} props - props containing the children to render if authenticated
 */
const AuthLoading: FunctionComponent<ContainerProps> = (props: ContainerProps) => {
    const [authFailed, setAuthFailed] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined as Error | undefined);

    useEffect(() => {
        if (!UserPermissions.instance.hasAuthorized && !authFailed) {
            UserPermissions.instance
                .authorize()
                .catch((err) => {
                    setError(err);
                    setAuthFailed(true);
                })
                .finally(() => {
                    if (UserPermissions.instance.hasPermission(PERMISSIONS.ITSE_HARDWARE_RESTOCK)) {
                        setLoading(false);
                    } else {
                        setError({
                            name: getString(`${getStringPath}.unauthorizedUser.name`),
                            message: getString(`${getStringPath}.unauthorizedUser.message`),
                        });
                        setAuthFailed(true);
                    }
                });
        }
    }, []);

    if (error) {
        return (
            <Box textAlign="center">
                <Alert
                    type="error"
                    header={getString(`${getStringPath}.failedMessage`)}
                    data-testid={AUTH_ERROR_TEST_ID}
                ></Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <Box textAlign="center">
                    <Header variant="h2" data-testid={AUTH_LOADING_TEST_ID}>
                        {getString(`${getStringPath}.loadingMessage`)}
                    </Header>
                    <Spinner size="large" />
                </Box>
            </div>
        );
    }
    return <Fragment>{props.children}</Fragment>;
};

export default AuthLoading;
